import React, { FC, ReactElement, useMemo } from 'react'
import { styled } from '~/app/components'
import OrderCardItem from '~/app/components/order-list/common/OrderCardItem'
import { CANCELLATION_STATUS, SERVICE_TYPE, statusToString } from '~/app/utils/OrderListUtils'
import { toKRW } from '~/app/utils/krNumberToString'
import { LeisureCancellationFeePlan, LeisureProductDetailInfo } from '~/__generated__/__types__'
import { CategoryType } from '~/__generated__/__types__'
import { TConvertedProductInfo } from '~/app/components/order-converter/converter/DomesticOrderConverter'
import { Typography } from '@yanolja-org/ya-com-core'
import { TConfigBadgeType } from '~/api/utils/leisure'
import { ConvertedOrder } from '~/app/components/order-converter/getCartOrderConverter'

/**
 * CancellationCardItem component
 * author: soojin.oh
 */

// type definitions
export type CancellationCardItemOrder = {
  id: string
  categoryOrderId: string
  status: CANCELLATION_STATUS
  title: string
  subTitle: string
  ratePlanTitle?: string
  info: TConvertedProductInfo
  options?: string
  refundPrice: number
  commissionAmount: number
  imageSrc: string | ReactElement
  serviceType: SERVICE_TYPE
  badges?: TConfigBadgeType[]
  isCancelPenalty?: boolean
  categoryType?: CategoryType
  refundInfo?: LeisureProductDetailInfo[]
  cancellationFeePlans?: LeisureCancellationFeePlan[]
} & Pick<ConvertedOrder, 'optionTitle'>
type Props = {
  order: CancellationCardItemOrder
}

// styled components
const Container = styled.div`
  padding: 2rem;

  ::after {
    content: '';
    border-bottom: 0.1rem solid ${({ theme }) => theme.color.gray200};
    position: relative;
    display: block;
    bottom: -2rem;
  }

  :nth-last-of-type(1) {
    ::after {
      content: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
    width: 100%;
  }
  @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
    width: 50%;
    margin: 0;
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    border-bottom: solid 1px ${({ theme }) => theme.color.gray200};
    :nth-of-type(2n - 1) {
      border-right: solid 0.1rem ${({ theme }) => theme.color.gray200};
    }

    :nth-last-of-type(1) {
      border-bottom: none;
    }

    :nth-last-of-type(2) {
      :nth-of-type(2n - 1) {
        border-bottom: none;
      }
    }

    ::after {
      content: none;
    }
  }
`

const RefundPrice = styled.div`
  text-align: right;
  width: 100%;

  & .label {
    color: ${({ theme: { color } }) => color.gray600};
    ${Typography('body2').regular};
    margin-right: 0.4rem;
  }

  & .price {
    color: ${({ theme: { color } }) => color.gray900};
    ${Typography('body1').bold}
  }

  & .cancel-completed {
    color: ${({ theme: { color } }) => color.gray400};
  }
`

const CommissionAmount = styled.div`
  text-align: right;
  width: 100%;

  &.cancel-completed {
    color: ${({ theme: { color } }) => color.gray400};
  }

  & .label {
    ${Typography('caption').regular};
    margin-right: 0.4rem;
  }

  & .price {
    ${Typography('body2').regular};
  }
`

// CancellationCardItem component
const CancellationCardItem: FC<Props> = function ({ order }) {
  const {
    id,
    categoryOrderId,
    status,
    title,
    subTitle,
    ratePlanTitle,
    info,
    options,
    refundPrice,
    commissionAmount,
    imageSrc,
    serviceType,
    badges,
    categoryType,
    optionTitle,
  } = order

  const { cancelStatusText, refundPriceClassName } = useMemo(
    () => ({
      cancelStatusText: status === CANCELLATION_STATUS.IN_PROGRESS ? '환불(예정)' : '예약 취소',
      refundPriceClassName: statusToString(status),
    }),
    [status]
  )

  return (
    <Container>
      <OrderCardItem
        id={id}
        categoryOrderId={categoryOrderId}
        serviceType={serviceType}
        categoryType={categoryType}
        status={status}
        title={title}
        subTitle={subTitle}
        optionTitle={optionTitle}
        ratePlanTitle={ratePlanTitle}
        info={info}
        options={options}
        style={{ padding: '0 0 1.2rem' }}
        imageSrc={imageSrc}
        badges={badges}
      />
      <div>
        <RefundPrice>
          <span className={`label ${refundPriceClassName}`}>{cancelStatusText}</span>
          <span className={`price ${refundPriceClassName}`}>{toKRW(refundPrice)}</span>
        </RefundPrice>
        <CommissionAmount className={statusToString(status)}>
          <span className="label">취소수수료</span>
          <span className="price">{toKRW(commissionAmount)}</span>
        </CommissionAmount>
      </div>
    </Container>
  )
}

export default CancellationCardItem
